import type { AppLayoutKind } from "@ui/layouts/layoutProvider";
import { useLayoutAppKind } from "@ui/layouts/layoutProvider";
import {
	agencyIdForBoardContext,
	clipContextForAppKind,
	useAddToBoard,
} from "@ui/molecules/AddToBoard/useAddToBoard";
import { toast } from "@ui/molecules/Toasts/toast";
import { getErrorMessage } from "@utils/getErrorMessage";
import {
	createContext,
	createEffect,
	createMemo,
	createResource,
	createRoot,
	createSignal,
	type ParentComponent,
	type Resource,
	useContext,
} from "solid-js";
import { createStore, produce } from "solid-js/store";
import {
	type BoardClipResponse,
	type BoardsByVideoIdsEntryResponse,
	type BoardsByVideoIdsResponse,
	getBoardsByVideoIds,
	getVideoById,
	getVideoSampledFrames,
	type ListBoardsData,
	type ListBoardsResponse,
	listBoards,
} from "@/client";
import type { EditClipEventDetail } from "~/modules/shared/chat/components/ChatLayout/ChatLayout";
import {
	clampClipTrim,
	LAST_BOARD_ID_KEY,
	NEW_CLIP_PLACEHOLDER_ID,
} from "./addToBoardContext";
import {
	type BoardListCacheEntry,
	upsertBoardInListResponse,
} from "./boardsListCache";

export type VideoBoardSegment = {
	video_id: string;
	start?: number;
	end?: number;
	video_duration?: number;
};

export type VideoEvidenceClipInput = {
	video_id: string;
	start_time?: number | null;
	end_time?: number | null;
	/** Full source video duration in seconds. */
	video_duration: number;
	/** Trimmed clip duration in seconds. */
	clip_duration: number;
	video_url: string;
	/** LangChain clip summary when the clip came from a question source. */
	title?: string | null;
	description?: string | null;
};

function clipTitleFromEvidence(clip: VideoEvidenceClipInput): string | undefined {
	const title = clip.title?.trim();
	if (title) {
		return title;
	}
	const description = clip.description?.trim();
	return description || undefined;
}

export type UseClipBoardMembershipOptions = {
	appEmbedded?: boolean;
	onAddedToBoard?: (board: BoardClipResponse, boardId: string) => void;
};

function roundSegmentSeconds(
	value: number | undefined | null,
): number | undefined {
	return value != null ? Math.round(value) : undefined;
}

function evidenceSegmentEnd(video: VideoBoardSegment): number {
	return (
		roundSegmentSeconds(video.end) ??
		roundSegmentSeconds(video.video_duration) ??
		0
	);
}

function segmentsForBoardQuery(videos: VideoBoardSegment[]) {
	return videos.map((video) => ({
		video_id: video.video_id,
		start: roundSegmentSeconds(video.start) ?? 0,
		end: evidenceSegmentEnd(video),
	}));
}

/** Canonical lookup key from evidence trim (unpadded, rounded). */
export function videoEvidenceBoardKey(clip: {
	video_id: string;
	start_time: number;
	end_time: number;
	video_duration: number;
}) {
	const start = roundSegmentSeconds(clip.start_time) ?? 0;
	const end =
		roundSegmentSeconds(clip.end_time) ??
		roundSegmentSeconds(clip.video_duration) ??
		0;
	return `${clip.video_id}:${start}:${end}`;
}

export function videoSegmentBoardKey(video: VideoBoardSegment) {
	const start = roundSegmentSeconds(video.start) ?? 0;
	const end = evidenceSegmentEnd(video);
	return `${video.video_id}:${start}:${end}`;
}

function boardsListCacheKey(query: BoardsListQuery): string {
	return JSON.stringify({
		search: query.search ?? "",
		context: query.context ?? "",
		project_id: query.project_id ?? "",
		agency_id: query.agency_id ?? "",
		page_size: query.page_size ?? "",
		page: query.page ?? "",
	});
}

function createBoardsStore() {
	const [clipMembership, setClipMembership] =
		createStore<BoardsByVideoIdsResponse>({});
	const [boardsListByQueryKey, setBoardsListByQueryKey] = createStore<
		Record<string, ListBoardsResponse>
	>({});
	const [boardsListVersion, setBoardsListVersion] = createSignal(0);
	const [boardsListFetchGeneration, setBoardsListFetchGeneration] =
		createSignal(0);
	const [clipMembershipVersion, setClipMembershipVersion] = createSignal(0);
	const [clipMembershipRefreshKey, setClipMembershipRefreshKey] =
		createSignal(0);

	const invalidateBoardsList = () => {
		setBoardsListByQueryKey({});
		setBoardsListFetchGeneration((generation) => generation + 1);
		setBoardsListVersion((version) => version + 1);
	};

	const upsertBoardInListCache = (
		board: BoardListCacheEntry,
		query: BoardsListQuery,
	) => {
		const key = boardsListCacheKey(query);
		setBoardsListByQueryKey(key, (current) =>
			upsertBoardInListResponse(current, board),
		);
		setBoardsListFetchGeneration((generation) => generation + 1);
		setBoardsListVersion((version) => version + 1);
	};

	const getCachedBoardsList = (query: BoardsListQuery) => {
		return boardsListByQueryKey[boardsListCacheKey(query)];
	};

	const cacheBoardsList = (query: BoardsListQuery, data: ListBoardsResponse) => {
		setBoardsListByQueryKey(boardsListCacheKey(query), data);
	};

	/** Request a refetch of clip membership for active `useClipBoardMembership` hooks. */
	const invalidateClipMembership = () => {
		setClipMembershipRefreshKey((version) => version + 1);
	};

	const refreshClipMembership = async (
		appKind: ReturnType<typeof useLayoutAppKind>,
		projectId: string | undefined,
		videos: VideoBoardSegment[],
	) => {
		if (videos.length === 0) {
			return;
		}
		const context = clipContextForAppKind(appKind);
		const agencyId = agencyIdForBoardContext(context);
		const querySegments = segmentsForBoardQuery(videos);
		try {
			const response = await getBoardsByVideoIds({
				query: {
					context,
					project_id: context === "gvtlabs" ? projectId : undefined,
					agency_id: agencyId,
					videos: JSON.stringify(querySegments),
				},
			});
			setClipMembership(
				produce((state) => {
					const data = response.data ?? {};
					for (const video of videos) {
						const lookupKey = videoSegmentBoardKey(video);
						const entry = data[lookupKey];
						if (entry) {
							state[lookupKey] = entry;
						} else {
							delete state[lookupKey];
						}
					}
				}),
			);
			setClipMembershipVersion((version) => version + 1);
		} catch {
			// Keep existing membership on transient failures.
		}
	};

	const getClipBoardEntry = (clip: {
		video_id: string;
		start_time: number;
		end_time: number;
		video_duration: number;
	}): BoardsByVideoIdsEntryResponse | undefined => {
		return clipMembership[videoEvidenceBoardKey(clip)];
	};

	const isClipOnBoard = (clip: {
		video_id: string;
		start_time: number;
		end_time: number;
		video_duration: number;
	}) => {
		const entry = getClipBoardEntry(clip);
		return !!(entry?.board && entry?.clip);
	};

	const selectClipMembership = (videos: VideoBoardSegment[]) => {
		const result: BoardsByVideoIdsResponse = {};
		for (const video of videos) {
			const key = videoSegmentBoardKey(video);
			const entry = clipMembership[key];
			if (entry) {
				result[key] = entry;
			}
		}
		return result;
	};

	return {
		boardsListVersion,
		boardsListFetchGeneration,
		clipMembershipVersion,
		clipMembershipRefreshKey,
		invalidateBoardsList,
		upsertBoardInListCache,
		invalidateClipMembership,
		getCachedBoardsList,
		cacheBoardsList,
		refreshClipMembership,
		getClipBoardEntry,
		isClipOnBoard,
		selectClipMembership,
		notifyBoardCreated: invalidateBoardsList,
		notifyBoardUpdated: invalidateBoardsList,
		notifyBoardDeleted: invalidateBoardsList,
		async notifyClipMembershipChanged(
			appKind: ReturnType<typeof useLayoutAppKind>,
			projectId: string | undefined,
			videos: VideoBoardSegment[],
		) {
			await refreshClipMembership(appKind, projectId, videos);
			invalidateBoardsList();
		},
	};
}

const boardsStore = createRoot(createBoardsStore);

const BoardsContext = createContext<ReturnType<typeof createBoardsStore>>();

export const BoardsProvider: ParentComponent = (props) => (
	<BoardsContext.Provider value={boardsStore}>
		{props.children}
	</BoardsContext.Provider>
);

export function useBoards() {
	return useContext(BoardsContext) ?? boardsStore;
}

export {
	AddToBoardContext,
	type BoardInfo,
	buildAddClipBody,
	clampClipTrim,
	type ClipAddSource,
	isClipUnsaved,
	LAST_BOARD_ID_KEY,
	type MoveClipTrim,
	NEW_CLIP_PLACEHOLDER_ID,
	type SaveClipFailureReason,
	type StoreType,
	saveClipErrorTitle,
	type UpdateBoardClipDonePayload,
} from "./addToBoardContext";
export { AddToBoardProvider } from "./addToBoardProvider";

export type BoardsListQuery = NonNullable<ListBoardsData["query"]>;

/** Board picker query, shared by every place that lets a user choose a board. */
export function boardsListQueryFor(params: {
	appKind: AppLayoutKind;
	projectId?: string;
	search?: string;
}): BoardsListQuery {
	const context = clipContextForAppKind(params.appKind);
	return {
		search: params.search ?? "",
		context,
		project_id: context === "roster" ? undefined : params.projectId,
		agency_id: agencyIdForBoardContext(context),
		page_size: 100,
	};
}

export async function fetchBoardsList(
	query: BoardsListQuery,
): Promise<ListBoardsResponse> {
	const res = await listBoards({ query });
	if (res.error) {
		throw new Error(getErrorMessage(res.error, "Unknown error"));
	}
	return res.data ?? {};
}

/** Reactive board list; refetches when `boardsListVersion` bumps. */
export function useBoardsList(
	query: () => BoardsListQuery,
): Resource<ListBoardsResponse> {
	const boards = useBoards();
	const [resource] = createResource(
		() => ({
			...query(),
			_version: boards.boardsListVersion(),
		}),
		async (args) => {
			const { _version: _ignored, ...queryArgs } = args;
			const generationAtStart = boards.boardsListFetchGeneration();
			const cached = boards.getCachedBoardsList(queryArgs);
			if (cached) {
				return cached;
			}
			const data = await fetchBoardsList(queryArgs);
			if (generationAtStart !== boards.boardsListFetchGeneration()) {
				return data;
			}
			boards.cacheBoardsList(queryArgs, data);
			return data;
		},
		{
			initialValue: boards.getCachedBoardsList(query()),
		},
	);
	return resource;
}

/** Reactive clip membership and add-to-board actions for video evidence segments. */
export function useClipBoardMembership(
	projectId: string | undefined,
	videos: VideoBoardSegment[],
	options?: UseClipBoardMembershipOptions,
) {
	const boards = useBoards();
	const appKind = useLayoutAppKind();

	const segmentKeys = createMemo(() =>
		videos.map((video) => videoSegmentBoardKey(video)),
	);

	const usedBoards = createMemo(() => {
		boards.clipMembershipVersion();
		return boards.selectClipMembership(videos);
	});

	const [hasLoadedMembership, setHasLoadedMembership] = createSignal(false);

	createEffect(() => {
		boards.clipMembershipRefreshKey();
		projectId;
		appKind;
		JSON.stringify(videos);
		setHasLoadedMembership(false);
		void boards.refreshClipMembership(appKind, projectId, videos).finally(() => {
			setHasLoadedMembership(true);
		});
	});

	const addClip = async (clip: VideoEvidenceClipInput) => {
		if (!sessionStorage.getItem(LAST_BOARD_ID_KEY)) {
			if (options?.appEmbedded) {
				return;
			}
			const frames = await getVideoSampledFrames({
				path: {
					video_id: clip.video_id,
				},
				query: {
					start: clip.start_time,
					end: clip.end_time,
					num_frames: 50,
				},
			});
			// `BoardClipResponse.duration` and `video.duration` are the source
			// video's duration, not the trimmed length. The move panel fetches
			// the video itself, but saveClipToBoard falls back to this clip's
			// `duration` while that fetch is still loading; buildAddClipBody then
			// pads the clip range inside it, and the clipping service rejects a
			// trim that ends more than 10s after that range. A missing duration
			// (0) falls through to the panel's own fallbacks.
			const sourceDuration = clip.video_duration || undefined;
			const trim = clampClipTrim(
				clip.start_time ?? 0,
				clip.end_time ?? undefined,
				clip.video_duration,
			);
			const moveEvent: EditClipEventDetail = {
				type: "open_move_board_panel",
				clip: {
					...clip,
					assets: [{ signed_url: clip.video_url, type: "preview_mp4" }],
					id: NEW_CLIP_PLACEHOLDER_ID,
					clipped_end: trim.end,
					clipped_start: trim.start,
					duration: sourceDuration,
					title: clipTitleFromEvidence(clip),
					sampled_frames: frames.data?.sampled_frames ?? [],
				},
				video: {
					id: clip.video_id,
					duration: sourceDuration,
				},
				board: {},
			};
			document.dispatchEvent(
				new CustomEvent("open_move_board_panel", {
					detail: moveEvent,
				}),
			);

			return;
		}

		const video = await getVideoById({
			path: {
				video_id: clip.video_id,
			},
		});
		const result = await useAddToBoard({
			video_id: clip.video_id,
			start_time: clip.start_time,
			end_time: clip.end_time,
			video_duration: video.data?.duration,
			clip_duration: clip.clip_duration,
			appKind,
			projectId,
			agencyId: agencyIdForBoardContext(clipContextForAppKind(appKind)),
			boardId: sessionStorage.getItem(LAST_BOARD_ID_KEY),
			title: clipTitleFromEvidence(clip),
		});
		if (result.duplicate) {
			toast.error({
				title: "This clip is already in that board",
			});
			return;
		}
		if (result.response.response.status === 200 && result.board) {
			await boards.notifyClipMembershipChanged(appKind, projectId, videos);
			options?.onAddedToBoard?.(
				result.board.data,
				String(result.response.data.board_id),
			);
			return;
		}
		if (result.response.error) {
			toast.error({
				title: "Failed to add to board",
				description: (result.response.error as { message?: string }).message,
			});
		}
	};

	return {
		usedBoards,
		getClipBoardEntry: (clip: {
			video_id: string;
			start_time: number;
			end_time: number;
			video_duration: number;
		}) => {
			segmentKeys();
			boards.clipMembershipVersion();
			return boards.getClipBoardEntry(clip);
		},
		isClipOnBoard: (clip: {
			video_id: string;
			start_time: number;
			end_time: number;
			video_duration: number;
		}) => {
			segmentKeys();
			boards.clipMembershipVersion();
			return boards.isClipOnBoard(clip);
		},
		refresh: () =>
			boards.notifyClipMembershipChanged(appKind, projectId, videos),
		isLoading: () => !hasLoadedMembership(),
		addClip,
	};
}
